export interface OverlayOptions {
	/** 1 = ordinary modal (z-index 1000), 2 = stacked on top via .level-1 (z-index 1001). */
	level?: 1 | 2;
	onEscape?: () => void;
	/** Only fires when the click target is the backdrop itself, not a panel descendant. */
	onBackdropClick?: () => void;
	/** Defaults to true. */
	mount?: boolean;
}

export interface OverlayHandle {
	element: HTMLDivElement;
	panel: HTMLDivElement;
	close(): void;
}

export function createOverlay(options: OverlayOptions = {}): OverlayHandle {
	const level = options.level ?? 1;
	const mount = options.mount ?? true;

	const element = document.createElement("div");
	element.className = level === 2 ? "modal-overlay level-1" : "modal-overlay";

	const panel = document.createElement("div");
	panel.className = "modal-panel";
	element.appendChild(panel);

	let escHandler: ((e: KeyboardEvent) => void) | null = null;
	let backdropClickHandler: ((e: MouseEvent) => void) | null = null;
	let closed = false;

	if (options.onEscape) {
		const onEscape = options.onEscape;
		escHandler = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				e.preventDefault();
				e.stopPropagation();
				onEscape();
			}
		};
		// Capture phase — fires before the game's keydown handler.
		document.addEventListener("keydown", escHandler, true);
	}

	if (options.onBackdropClick) {
		const onBackdropClick = options.onBackdropClick;
		backdropClickHandler = (e: MouseEvent) => {
			if (e.target === element) {
				onBackdropClick();
			}
		};
		element.addEventListener("click", backdropClickHandler);
	}

	function close(): void {
		if (closed) return;
		closed = true;
		if (escHandler) {
			document.removeEventListener("keydown", escHandler, true);
			escHandler = null;
		}
		if (backdropClickHandler) {
			element.removeEventListener("click", backdropClickHandler);
			backdropClickHandler = null;
		}
		if (element.parentNode) {
			element.parentNode.removeChild(element);
		}
	}

	if (mount) {
		document.body.appendChild(element);
	}

	return { element, panel, close };
}
